export const PDF_US_LETTER = Object.freeze({ width: 612, height: 792 });
/**
 * Shared DOM/HTML escape helpers — single source of truth for
 * `cssEscape` (CSS attribute-selector escaping) and `escHtml` (HTML
 * text/attribute escaping). Editor modules MUST import from here
 * rather than defining their own copies (silent-drift hazard).
 */
export function cssEscape(s) {
    if (typeof CSS !== "undefined" && CSS.escape)
        return CSS.escape(s);
    return String(s).replace(/(["\\])/g, "\\$1");
}
export function escHtml(s) {
    // WHY: the editor is an ES module tree and cannot consume window.adatool.esc
    // (global-script load order cannot be guaranteed at module import time).
    // This function is intentionally identical to window.adatool.esc in esc.js —
    // the two are kept in sync; do NOT diverge. See esc.js for the canonical
    // implementation note and esc-canonical.test.mjs for parity tests.
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
/**
 * Look up a required DOM element by ID. Throws if missing.
 *
 * Use at editor init time for elements that are present in index.html and
 * load-bearing for the editor to function. Centralises the "throw once at
 * init" discipline so call sites can assume the element exists and skip
 * defensive null-checks. The optional generic parameter narrows the return
 * type for `.value` / `.disabled` / `.checked` / `.click()` access without
 * an inline cast.
 *
 *   const saveBtn = getRequired<HTMLButtonElement>("editor-save-btn");
 *   saveBtn.disabled = true;
 *
 * If an element is only sometimes present (e.g., gated by a feature flag),
 * use `document.getElementById(...)` directly instead — getRequired's
 * "throw on missing" contract is for the should-always-exist set.
 */
export function getRequired(id) {
    const el = document.getElementById(id); // noqa: no-global-read-in-frontend-production — canonical-dom-accessor-module
    if (el === null) {
        throw new Error(`Required DOM element not found: #${id}`);
    }
    return el;
}
/**
 * Look up a required DOM element by CSS selector. Throws if missing.
 *
 * Companion to getRequired for cases where the element is keyed off a
 * class or attribute selector rather than a unique ID (canonical example:
 * the `.editor-empty-card` host in editor.ts).
 */
export function querySelectorRequired(selector, root = document) {
    const el = root.querySelector(selector);
    if (el === null) {
        throw new Error(`Required DOM element not found: ${selector}`);
    }
    return el;
}
